import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QrCode, Loader2, Users, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/team/invite/${token}`);
        setInvite(data);
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    setErr("");
    try {
      await api.post("/team/accept", { token });
      toast.success(`Welcome to ${invite.workspace_name}!`);
      await refreshMe?.();
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="max-w-md mx-auto w-full px-4 sm:px-6 flex-1 flex flex-col justify-center py-12">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow">
            <QrCode className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-2xl tracking-tight text-slate-900">Qdurator</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          {!invite ? (
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Invite not found
              </h1>
              <p className="text-sm text-slate-500 mt-2">
                {err || "This invite link is invalid or has expired. Ask the person who invited you to send a new one."}
              </p>
              <div className="mt-6">
                <Link to="/">
                  <Button variant="outline" className="rounded-xl">Back to home</Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900" data-testid="accept-invite-heading">
                You've been invited
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{invite.inviter_name || "A teammate"}</span>{" "}
                invited <span className="font-semibold text-slate-900">{invite.email}</span> to join{" "}
                <span className="font-semibold text-slate-900">{invite.workspace_name}</span> as a {invite.role}.
              </p>

              {err && (
                <div className="mt-4 text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg" data-testid="accept-invite-error">
                  {err}
                </div>
              )}

              {user ? (
                user.email !== invite.email ? (
                  <div className="mt-6 space-y-3">
                    <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg">
                      You're logged in as <b>{user.email}</b>, but this invite is for <b>{invite.email}</b>. Log out and log in with that email to accept.
                    </div>
                    <Link to="/dashboard">
                      <Button variant="outline" className="w-full rounded-xl">
                        Back to dashboard
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="mt-6">
                    <Button
                      onClick={accept}
                      disabled={accepting}
                      data-testid="accept-invite-btn"
                      className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    >
                      {accepting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>Accept invite <ArrowRight className="w-4 h-4 ml-1" /></>
                      )}
                    </Button>
                  </div>
                )
              ) : (
                <div className="mt-6 space-y-3">
                  <p className="text-sm text-slate-500">
                    Sign up or log in with <b>{invite.email}</b> to accept.
                  </p>
                  <Link to={`/register?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(invite.email)}`}>
                    <Button className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="accept-invite-register-btn">
                      Create account
                    </Button>
                  </Link>
                  <Link to={`/login?next=/accept-invite/${token}`}>
                    <Button variant="outline" className="w-full h-11 rounded-xl">
                      I already have an account
                    </Button>
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
